import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AnimatedProgress } from './AnimatedProgress';
import { useGoals, Goal } from '@/hooks/use-goals';
import { Target, ArrowRight, PiggyBank } from 'lucide-react';

interface ActiveGoalsWidgetProps {
  maxItems?: number;
}

export function ActiveGoalsWidget({ maxItems = 4 }: ActiveGoalsWidgetProps) {
  const navigate = useNavigate();
  const { goals, loading } = useGoals();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);

  // Sort by relevance: closest to target date, then by progress
  const sortedGoals = [...goals]
    .filter((g) => g.is_active && g.current_amount < g.target_amount)
    .sort((a, b) => {
      // Priority 1: Has target date
      if (a.target_date && !b.target_date) return -1;
      if (!a.target_date && b.target_date) return 1;
      if (a.target_date && b.target_date) {
        return new Date(a.target_date).getTime() - new Date(b.target_date).getTime();
      }
      // Priority 2: Higher progress percentage
      const progressA = a.current_amount / a.target_amount;
      const progressB = b.current_amount / b.target_amount;
      return progressB - progressA;
    })
    .slice(0, maxItems);

  if (loading) {
    return (
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="h-4 w-4" />
            Metas Ativas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded" />
            <div className="h-4 bg-muted rounded w-3/4" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (goals.length === 0) {
    return (
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="h-4 w-4" />
            Metas Ativas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-2">
            <PiggyBank className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              Crie seu primeiro cofrinho
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/goals')}
            >
              Criar Cofrinho
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="h-4 w-4" />
            Metas Ativas
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/goals')}
            className="text-xs"
          >
            Ver todos
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {sortedGoals.map((goal) => (
          <div key={goal.id} className="space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="font-medium truncate">{goal.name}</span>
              <span className="text-muted-foreground text-xs">
                {Math.round((goal.current_amount / goal.target_amount) * 100)}%
              </span>
            </div>
            <AnimatedProgress
              value={goal.current_amount}
              max={goal.target_amount}
              size="sm"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{formatCurrency(goal.current_amount)}</span>
              <span>{formatCurrency(goal.target_amount)}</span>
            </div>
          </div>
        ))}

        {goals.length > maxItems && (
          <p className="text-xs text-muted-foreground text-center">
            +{goals.length - maxItems} cofrinhos
          </p>
        )}
      </CardContent>
    </Card>
  );
}
